import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { Plane, Plus, X } from 'lucide-react';
import { formatINR } from '../../../lib/format';
import { useAuth } from '../../../contexts/AuthContext';
import EmployeeSelect from '../../../components/pickers/EmployeeSelect';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600 border-slate-300',
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
  completed: 'bg-teal-50 text-teal-700 border-teal-200',
  cancelled: 'bg-slate-100 text-slate-400 border-slate-300',
};
const MODES = ['flight', 'train', 'bus', 'own_vehicle', 'company_vehicle', 'taxi'];

export default function TravelRequests() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('mine');
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isHR = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD';

  const load = useCallback(() => {
    if (tab === 'approvals') apiGet('/hr/travel/requests', { status: 'pending_approval' }).then((r) => setItems(r.items || [])).catch(() => {});
    else apiGet('/hr/travel/requests', { mine: true }).then((r) => setItems(r.items || [])).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="travel-requests-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">HR · Admin</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Travel Requests</h1>
          <p className="text-sm text-slate-600 mt-1">Raise travel requests and approve your team's.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="new-travel-btn" className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2"><Plus size={14} /> New Request</button>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab('mine')} data-testid="tab-mine" className={`px-3 py-1.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider border ${tab === 'mine' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-200'}`}>My Requests</button>
        <button onClick={() => setTab('approvals')} data-testid="tab-approvals" className={`px-3 py-1.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider border ${tab === 'approvals' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-200'}`}>Approvals Queue</button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="travel-empty"><Plane size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">No travel requests.</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {['Employee', 'Route', 'Departure', 'Mode', 'Est. Cost', 'Status', ''].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/app/hr/travel/requests/${r.id}`)} data-testid={`travel-row-${r.id}`}>
                  <td className="px-3 py-2 font-display font-bold">{r.employee_name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.from_location || '?'} → {r.to_location || '?'}</td>
                  <td className="px-3 py-2 text-slate-500">{r.departure_date || '—'}</td>
                  <td className="px-3 py-2 text-slate-500">{r.mode}</td>
                  <td className="px-3 py-2">{r.estimated_cost ? formatINR(r.estimated_cost) : '—'}</td>
                  <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${STATUS_COLORS[r.status]}`}>{r.status.replace('_', ' ')}</span></td>
                  <td className="px-3 py-2 text-[#1F7A6E] font-bold">Open →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <TravelForm isHR={isHR} onClose={() => setShowForm(false)} onSaved={(id) => { setShowForm(false); nav(`/app/hr/travel/requests/${id}`); }} />}
    </div>
  );
}

function TravelForm({ isHR, onClose, onSaved }) {
  const [f, setF] = useState({ request_type: 'domestic', purpose: '', from_location: '', to_location: '', departure_date: '', return_date: '', mode: 'flight', estimated_cost: '', advance_requested: '', accommodation_required: false });
  const [onBehalf, setOnBehalf] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.from_location || !f.to_location) { toast.error('From and To locations are required'); return; }
    setBusy(true);
    try {
      const payload = { ...f, estimated_cost: f.estimated_cost ? Number(f.estimated_cost) : null, advance_requested: f.advance_requested ? Number(f.advance_requested) : 0 };
      if (onBehalf) payload.employee_id = onBehalf.id;
      const r = await apiPost('/hr/travel/requests', payload);
      toast.success('Travel request created (draft)'); onSaved(r.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-testid="travel-form-drawer">
        <header className="px-5 py-4 border-b flex items-center justify-between bg-[#1F7A6E] text-white"><h2 className="font-display font-bold tracking-wide uppercase text-sm">New Travel Request</h2><button onClick={onClose}><X size={20} /></button></header>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-3">
          {isHR && <div><span className="lbl">Raise on behalf of (optional)</span><EmployeeSelect value={onBehalf} onSelect={setOnBehalf} testid="travel-onbehalf" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="lbl">Type</span><select className="inp" value={f.request_type} onChange={(e) => setF({ ...f, request_type: e.target.value })}><option value="domestic">Domestic</option><option value="international">International</option></select></label>
            <label className="block"><span className="lbl">Mode</span><select className="inp" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>{MODES.map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}</select></label>
          </div>
          <label className="block"><span className="lbl">Purpose</span><input className="inp" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="travel-purpose" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="lbl">From *</span><input className="inp" value={f.from_location} onChange={(e) => setF({ ...f, from_location: e.target.value })} data-testid="travel-from" /></label>
            <label className="block"><span className="lbl">To *</span><input className="inp" value={f.to_location} onChange={(e) => setF({ ...f, to_location: e.target.value })} data-testid="travel-to" /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="lbl">Departure</span><input type="date" className="inp" value={f.departure_date} onChange={(e) => setF({ ...f, departure_date: e.target.value })} data-testid="travel-departure" /></label>
            <label className="block"><span className="lbl">Return</span><input type="date" className="inp" value={f.return_date} onChange={(e) => setF({ ...f, return_date: e.target.value })} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="lbl">Est. Cost (₹)</span><input type="number" className="inp" value={f.estimated_cost} onChange={(e) => setF({ ...f, estimated_cost: e.target.value })} /></label>
            <label className="block"><span className="lbl">Advance (₹)</span><input type="number" className="inp" value={f.advance_requested} onChange={(e) => setF({ ...f, advance_requested: e.target.value })} /></label>
          </div>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.accommodation_required} onChange={(e) => setF({ ...f, accommodation_required: e.target.checked })} /> Accommodation required</label>
          <button type="submit" disabled={busy} data-testid="travel-submit" className="w-full py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] disabled:opacity-50">{busy ? 'Saving…' : 'Create Request'}</button>
        </form>
        <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#1F7A6E}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
      </div>
    </>
  );
}
